<template>
  <div class="ocr-editable-grid">
    <div v-if="!localColumns.length" class="ocr-empty-cell">No table data to edit.</div>
    <template v-else>
      <div class="ocr-editable-grid-toolbar">
        <v-btn size="small" variant="text" class="ocr-toolbar-btn" :disabled="!canUndo" @click="undo">Undo</v-btn>
        <v-btn size="small" variant="text" class="ocr-toolbar-btn" :disabled="!canRedo" @click="redo">Redo</v-btn>
        <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" @click="addRow">Add row</v-btn>
        <v-btn
          size="small"
          variant="outlined"
          class="ocr-toolbar-btn"
          :disabled="!selectedRows.length"
          @click="deleteRows"
        >
          Delete rows ({{ selectedRows.length }})
        </v-btn>
        <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" @click="addColumn">Add column</v-btn>
        <v-btn
          size="small"
          variant="outlined"
          class="ocr-toolbar-btn"
          :disabled="localColumns.length < 2 || selectedCol == null"
          @click="deleteColumn"
        >
          Delete column
        </v-btn>
        <v-spacer />
        <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="saving" @click="emitSave">
          Save edits
        </v-btn>
      </div>
      <OcrTableWrap>
        <v-table class="well-table sample-table well-table-data fd-table">
          <thead>
            <tr class="main-header">
              <th class="ocr-select-col"></th>
              <th
                v-for="(col, cIdx) in localColumns"
                :key="col + cIdx"
                class="ocr-editable-col-head"
                :class="{ 'ocr-editable-col-head--active': selectedCol === cIdx }"
                @click="selectedCol = cIdx"
              >
                <input
                  v-model="localColumns[cIdx]"
                  class="ocr-editable-grid-input ocr-editable-col-input"
                  type="text"
                  :aria-label="`Column ${cIdx + 1} name`"
                  @click.stop
                  @focus="onCellFocus"
                  @input="onCellInput"
                  @blur="onCellBlur"
                />
              </th>
            </tr>
          </thead>
          <tbody class="well-table-content">
            <tr
              v-for="(row, rIdx) in localData"
              :key="rIdx"
              :class="{ 'ocr-table-pick-row--active': selectedRows.includes(rIdx) }"
            >
              <td class="ocr-select-col">
                <v-checkbox
                  :model-value="selectedRows.includes(rIdx)"
                  hide-details
                  density="compact"
                  @update:model-value="toggleRow(rIdx)"
                />
              </td>
              <td v-for="(col, cIdx) in localColumns" :key="col + rIdx">
                <input
                  v-model="localData[rIdx][cIdx]"
                  class="ocr-editable-grid-input"
                  type="text"
                  @focus="onCellFocus"
                  @input="onCellInput"
                  @blur="onCellBlur"
                />
              </td>
            </tr>
          </tbody>
        </v-table>
      </OcrTableWrap>
    </template>
  </div>
</template>

<script>
import OcrTableWrap from "@/components/layout/OCR/OcrTableWrap.vue";

export default {
  name: "OcrEditableGrid",
  components: { OcrTableWrap },
  props: {
    grid: { type: Object, default: null },
    saving: { type: Boolean, default: false },
  },
  emits: ["save"],
  data() {
    return {
      localColumns: [],
      localData: [],
      selectedRows: [],
      selectedCol: null,
      undoStack: [],
      redoStack: [],
      pendingHistory: null,
    };
  },
  computed: {
    canUndo() {
      return this.undoStack.length > 0;
    },
    canRedo() {
      return this.redoStack.length > 0;
    },
  },
  watch: {
    grid: {
      immediate: true,
      deep: true,
      handler() {
        this.syncFromGrid();
      },
    },
  },
  methods: {
    syncFromGrid() {
      const g = this.grid;
      if (!g || !g.columns || !g.columns.length) {
        this.localColumns = [];
        this.localData = [];
        this.selectedRows = [];
        this.selectedCol = null;
        return;
      }
      this.localColumns = [...g.columns];
      this.localData = (g.data || []).map((row) =>
        this.localColumns.map((_, i) => (row[i] != null ? String(row[i]) : "")),
      );
      this.selectedRows = [];
      this.undoStack = [];
      this.redoStack = [];
      this.pendingHistory = null;
    },
    snapshot() {
      return {
        columns: [...this.localColumns],
        data: this.localData.map((row) => [...row]),
        selectedRows: [...this.selectedRows],
        selectedCol: this.selectedCol,
      };
    },
    applySnapshot(snap) {
      this.localColumns = [...snap.columns];
      this.localData = snap.data.map((row) => [...row]);
      this.selectedRows = [...(snap.selectedRows || [])];
      this.selectedCol = snap.selectedCol ?? null;
      this.pendingHistory = null;
    },
    pushHistory() {
      this.undoStack.push(this.pendingHistory || this.snapshot());
      if (this.undoStack.length > 50) this.undoStack.shift();
      this.redoStack = [];
      this.pendingHistory = null;
    },
    undo() {
      if (!this.undoStack.length) return;
      this.redoStack.push(this.snapshot());
      this.applySnapshot(this.undoStack.pop());
    },
    redo() {
      if (!this.redoStack.length) return;
      this.undoStack.push(this.snapshot());
      this.applySnapshot(this.redoStack.pop());
    },
    onCellFocus() {
      if (!this.pendingHistory) this.pendingHistory = this.snapshot();
    },
    onCellInput() {
      if (!this.pendingHistory) return;
      this.pushHistory();
    },
    onCellBlur() {
      this.pendingHistory = null;
    },
    uniqueColumnName(base = "Column") {
      const existing = new Set(this.localColumns.map((c) => String(c)));
      let i = this.localColumns.length + 1;
      let name = `${base} ${i}`;
      while (existing.has(name)) {
        i += 1;
        name = `${base} ${i}`;
      }
      return name;
    },
    addRow() {
      this.pushHistory();
      const insertAt = this.selectedRows.length
        ? Math.max(...this.selectedRows) + 1
        : this.localData.length;
      const blank = this.localColumns.map(() => "");
      this.localData.splice(insertAt, 0, blank);
      this.selectedRows = [insertAt];
    },
    deleteRows() {
      if (!this.selectedRows.length) return;
      this.pushHistory();
      const drop = new Set(this.selectedRows);
      this.localData = this.localData.filter((_, i) => !drop.has(i));
      this.selectedRows = [];
    },
    addColumn() {
      this.pushHistory();
      const name = this.uniqueColumnName();
      const at = this.selectedCol == null ? this.localColumns.length : this.selectedCol + 1;
      this.localColumns.splice(at, 0, name);
      this.localData = this.localData.map((row) => {
        const next = [...row];
        next.splice(at, 0, "");
        return next;
      });
      this.selectedCol = at;
    },
    deleteColumn() {
      if (this.selectedCol == null || this.localColumns.length < 2) return;
      this.pushHistory();
      const at = this.selectedCol;
      this.localColumns.splice(at, 1);
      this.localData = this.localData.map((row) => row.filter((_, i) => i !== at));
      this.selectedCol = Math.min(at, this.localColumns.length - 1);
    },
    toggleRow(idx) {
      const set = new Set(this.selectedRows);
      if (set.has(idx)) set.delete(idx);
      else set.add(idx);
      this.selectedRows = [...set].sort((a, b) => a - b);
    },
    emitSave() {
      const grid = {
        columns: [...this.localColumns],
        data: this.localData.map((row) => this.localColumns.map((_, i) => (row[i] != null ? row[i] : ""))),
      };
      this.$emit("save", grid);
    },
  },
};
</script>
